import { computed, defineComponent, inject, provide, shallowRef } from 'vue';

import type { VueNode } from '@components/utils';
import type { NTree } from '@components/components';
import type { ComputedRef, InjectionKey, ShallowRef, PropType } from 'vue';

export interface TreeContextProps {
  selectable: boolean;
  showIcon: boolean;
  icon: NTree.TIconType;
  switcherIcon: NTree.TIconType;
  draggable: NTree.TDraggableConfig | false;
  draggingNodeKey?: NTree.TKey;
  checkable: boolean;
  customCheckable: () => any;
  checkStrictly: boolean;
  disabled: boolean;
  keyEntities: Record<NTree.TKey, NTree.IDataEntity<any>>;
  dropLevelOffset?: number;
  dropContainerKey: NTree.TKey | null;
  dropTargetKey: NTree.TKey | null;
  dropPosition: -1 | 0 | 1 | null;
  indent: number | null;
  dropIndicatorRender: (props: {
    dropPosition: -1 | 0 | 1 | null;
    dropLevelOffset?: number;
    indent: number | null;
    prefixCls: string;
  }) => VueNode;
  dragOverNodeKey: NTree.TKey | null;
  dragging: boolean;

  loadData?: (treeNode: NTree.IEventDataNode) => Promise<void>;
  filterTreeNode: (treeNode: NTree.IEventDataNode) => boolean;

  onNodeClick: NTree.TNodeMouseEventHandler;
  onNodeDoubleClick: NTree.TNodeMouseEventHandler;
  onNodeExpand: NTree.TNodeMouseEventHandler;
  onNodeSelect: NTree.TNodeMouseEventHandler;
  onNodeCheck: (e: MouseEvent, treeNode: NTree.IEventDataNode, checked: boolean) => void;
  onNodeLoad: (treeNode: NTree.IEventDataNode) => void;
  onNodeMouseEnter: NTree.TNodeMouseEventHandler;
  onNodeMouseLeave: NTree.TNodeMouseEventHandler;
  onNodeContextMenu: NTree.TNodeMouseEventHandler;
  onNodeDragStart: NTree.TNodeDragEventHandler;
  onNodeDragEnter: NTree.TNodeDragEventHandler;
  onNodeDragOver: NTree.TNodeDragEventHandler;
  onNodeDragLeave: NTree.TNodeDragEventHandler;
  onNodeDragEnd: NTree.TNodeDragEventHandler;
  onNodeDrop: NTree.TNodeDragEventHandler;
  slots: {
    title?: (data: any) => any;
    titleRender?: (data: any) => any;
    [key: string]: ((...args: any[]) => any) | undefined;
  };
}

const TreeContextKey: InjectionKey<ComputedRef<TreeContextProps>> = Symbol('cds:tree-context');

export const TreeContext = defineComponent({
  name: 'TreeContext',
  props: {
    value: { type: Object as PropType<TreeContextProps> }
  },
  setup(props, { slots }) {
    provide(
      TreeContextKey,
      computed(() => props.value as TreeContextProps)
    );

    return () => slots.default?.();
  }
});

export const useInjectTreeContext = () => {
  return inject(
    TreeContextKey,
    computed(() => ({} as TreeContextProps))
  );
};

type KeysStateKeyType = {
  expandedKeysSet: ComputedRef<Set<NTree.TKey>>;
  selectedKeysSet: ComputedRef<Set<NTree.TKey>>;
  loadedKeysSet: ComputedRef<Set<NTree.TKey>>;
  loadingKeysSet: ComputedRef<Set<NTree.TKey>>;
  checkedKeysSet: ComputedRef<Set<NTree.TKey>>;
  halfCheckedKeysSet: ComputedRef<Set<NTree.TKey>>;
  expandedKeys: ShallowRef<NTree.TKey[]>;
  selectedKeys: ShallowRef<NTree.TKey[] | undefined>;
  loadedKeys: ShallowRef<NTree.TKey[]>;
  loadingKeys: ShallowRef<NTree.TKey[]>;
  checkedKeys: ShallowRef<NTree.TKey[]>;
  halfCheckedKeys: ShallowRef<NTree.TKey[]>;
  flattenNodes: ShallowRef<NTree.IFlattenNode[]>;
};

const KeysStateKey: InjectionKey<KeysStateKeyType> = Symbol('cds:keys-state');
export const useProvideKeysState = (state: KeysStateKeyType) => {
  provide(KeysStateKey, state);
};

export const useInjectKeysState = () => {
  return inject(KeysStateKey, {
    expandedKeys: shallowRef<NTree.TKey[]>([]),
    selectedKeys: shallowRef<NTree.TKey[]>([]),
    loadedKeys: shallowRef<NTree.TKey[]>([]),
    loadingKeys: shallowRef<NTree.TKey[]>([]),
    checkedKeys: shallowRef<NTree.TKey[]>([]),
    halfCheckedKeys: shallowRef<NTree.TKey[]>([]),
    expandedKeysSet: computed<Set<NTree.TKey>>(() => new Set()),
    selectedKeysSet: computed<Set<NTree.TKey>>(() => new Set()),
    loadedKeysSet: computed<Set<NTree.TKey>>(() => new Set()),
    loadingKeysSet: computed<Set<NTree.TKey>>(() => new Set()),
    checkedKeysSet: computed<Set<NTree.TKey>>(() => new Set()),
    halfCheckedKeysSet: computed<Set<NTree.TKey>>(() => new Set()),
    flattenNodes: shallowRef<NTree.IFlattenNode[]>([])
  });
};
